<script lang="ts">
  /// 統合「メンバー」一覧テーブル (ACC-7)。担当者 (app_accounts) を正面に Auth0 在籍状態を
  /// 併記し、行アクションを出す純表示ウィジェット。メンバー画面と組織詳細画面で共有する。
  ///
  /// FSD: widget は判断を持たず props でデータ + ケイパビリティ + ハンドラを受け取る。
  /// 「誰が何を操作できるか」の判断 (canChangeRoleOf 等) はページが session から算出して注入し、
  /// 真実は backend。ハンドラ未指定のボタンは出さない (例: メンバー画面は除名を渡さない)。
  import { accountRoleLabel, type AccountStatus } from '@shared/types/account';
  import type { Member } from '@shared/types/member';
  import KebabMenu from '@shared/ui/KebabMenu.svelte';

  interface Props {
    members: Member[];
    /// 招待 / ロール変更が可能か (super_admin / org_admin)。ロール変更ボタンの基本ゲート。
    allowManageMembers: boolean;
    /// テナント操作 (無効化・再有効化・除名・認証情報削除) が可能か (super_admin 専用)。
    allowManageTenant: boolean;
    /// この行のメンバーのロールを変更できるか (被害者保護ガードの UI ミラー)。
    canChangeRoleOf: (member: Member) => boolean;
    /// ログイン中ユーザー自身の account_id (= 自分の行)。自己無効化/自己失効は
    /// 不可逆な締め出しになるため、自分の行ではこれらを出さない (真の防御は backend)。
    currentAccountId?: string | null;
    onChangeRole?: (member: Member) => void;
    onDeactivate?: (member: Member) => void;
    onReactivate?: (member: Member) => void;
    /// Auth0 組織からの除名。組織詳細画面のみ供給する (メンバー画面では渡さない)。
    onRemoveFromOrg?: (member: Member) => void;
    /// invited な担当者の招待取消。供給時のみ invited 行に「招待取消」を出す。
    onCancelInvitation?: (member: Member) => void;
    /// 認証情報の削除 (失効)。Auth0 user を削除しログインを恒久抹消する。供給時のみ出す。
    onRevokeCredentials?: (member: Member) => void;
  }

  const {
    members,
    allowManageMembers,
    allowManageTenant,
    canChangeRoleOf,
    currentAccountId,
    onChangeRole,
    onDeactivate,
    onReactivate,
    onRemoveFromOrg,
    onCancelInvitation,
    onRevokeCredentials,
  }: Props = $props();

  // 自分の行か (自己無効化/自己失効の抑止に使う)。
  function isSelf(member: Member): boolean {
    return !!currentAccountId && member.id === currentAccountId;
  }

  const statusLabels: Record<AccountStatus, string> = {
    invited: '招待中',
    active: '有効',
    inactive: '無効',
  };

  // ロール変更ボタンを出すか (ハンドラ供給時。可否は disabled で表現)。
  function showChangeRole(): boolean {
    return !!onChangeRole;
  }
  function canChangeRole(member: Member): boolean {
    return allowManageMembers && member.auth0.has_identity && canChangeRoleOf(member);
  }

  // 除名は「Auth0 org member の行」にのみ出す。未ログイン / 未参加は org membership が無く
  // 除名対象にならない。super_admin 専用 (allowManageTenant) + ハンドラ供給時のみ。
  function canRemoveFromOrg(member: Member): boolean {
    return !!onRemoveFromOrg && allowManageTenant && member.auth0.is_org_member;
  }

  // 招待取消は invited 行にのみ出す。招待 / ロール変更と同じ権限 (allowManageMembers) +
  // 被害者保護 (canChangeRoleOf: org_admin は super_admin 招待を取り消せない) で出し分ける。
  function canCancelInvitation(member: Member): boolean {
    return (
      !!onCancelInvitation &&
      allowManageMembers &&
      member.status === 'invited' &&
      canChangeRoleOf(member)
    );
  }

  // 無効化: active な行のみ (super_admin 専用)。自分の行は出さない (自己締め出し防止)。
  function canDeactivate(member: Member): boolean {
    return (
      !!onDeactivate && allowManageTenant && member.status === 'active' && !isSelf(member)
    );
  }
  // 再有効化: inactive かつ identity を持つ行のみ (= 無効化/block されただけ)。
  // identity の無い inactive (失効済み = Auth0 user 削除済み) は再有効化では戻せず、
  // 復帰は再招待で行うため出さない。自分の行は出さない (block の ~1h トークン窓で本人が
  // 自己の無効化を巻き戻すのを防ぐ。canDeactivate / canRevokeCredentials と対称)。
  function canReactivate(member: Member): boolean {
    return (
      !!onReactivate &&
      allowManageTenant &&
      member.status === 'inactive' &&
      member.auth0.has_identity &&
      !isSelf(member)
    );
  }
  // 認証情報削除 (失効): ログイン実体 (identity) を持つ active / inactive のみ。
  // invited (未ログイン) は招待取消で消す。super_admin 専用。
  function canRevokeCredentials(member: Member): boolean {
    return (
      !!onRevokeCredentials &&
      allowManageTenant &&
      member.auth0.has_identity &&
      (member.status === 'active' || member.status === 'inactive') &&
      !isSelf(member)
    );
  }

  // この行に出せる操作が 1 つでもあるか (無ければケバブを無効化する)。
  function hasAnyAction(member: Member): boolean {
    return (
      showChangeRole() ||
      canDeactivate(member) ||
      canReactivate(member) ||
      canRemoveFromOrg(member) ||
      canCancelInvitation(member) ||
      canRevokeCredentials(member)
    );
  }
</script>

<table class="data-table">
  <thead>
    <tr>
      <th>氏名</th>
      <th>メール</th>
      <th>ロール</th>
      <th>状態</th>
      <th>Auth0 在籍</th>
      <th class="actions">操作</th>
    </tr>
  </thead>
  <tbody>
    {#each members as member (member.id)}
      <tr>
        <td>{member.display_name}</td>
        <td>{member.primary_email ?? '—'}</td>
        <td>{accountRoleLabel(member.current_role)}</td>
        <td>
          <span class="badge status-{member.status}">{statusLabels[member.status]}</span>
        </td>
        <td>
          {#if !member.auth0.has_identity}
            <span class="badge auth0-none" title="まだ Auth0 にログインしていません">未ログイン</span>
          {:else}
            <span
              class="badge auth0-{member.auth0.is_org_member ? 'member' : 'nonmember'}"
              title={member.auth0.is_org_member
                ? 'Auth0 組織のメンバーです'
                : 'Auth0 組織に未参加です'}
            >
              {member.auth0.is_org_member ? '在籍' : '未参加'}
            </span>
            {#if member.auth0.blocked}
              <span class="badge auth0-blocked" title="Auth0 でブロック中">ブロック</span>
            {/if}
          {/if}
        </td>
        <td class="actions">
          <KebabMenu disabled={!hasAnyAction(member)}>
            {#if showChangeRole()}
              <button
                type="button"
                class="menu-item"
                disabled={!canChangeRole(member)}
                title={!canChangeRoleOf(member)
                  ? '上位ロールのメンバーは変更できません'
                  : member.auth0.has_identity
                    ? ''
                    : '未ログインのため Auth0 ロールを変更できません'}
                onclick={() => onChangeRole?.(member)}
              >
                ロール変更
              </button>
            {/if}
            {#if canReactivate(member)}
              <button type="button" class="menu-item" onclick={() => onReactivate?.(member)}>
                再有効化
              </button>
            {/if}
            {#if canDeactivate(member)}
              <button
                type="button"
                class="menu-item danger"
                onclick={() => onDeactivate?.(member)}
              >
                無効化
              </button>
            {/if}
            {#if canCancelInvitation(member)}
              <button
                type="button"
                class="menu-item danger"
                onclick={() => onCancelInvitation?.(member)}
              >
                招待取消
              </button>
            {/if}
            {#if canRemoveFromOrg(member)}
              <button
                type="button"
                class="menu-item danger"
                onclick={() => onRemoveFromOrg?.(member)}
              >
                組織から外す
              </button>
            {/if}
            {#if canRevokeCredentials(member)}
              <button
                type="button"
                class="menu-item danger"
                title="Auth0 ユーザーを削除しログインを恒久的に無効化します (履歴は残ります)"
                onclick={() => onRevokeCredentials?.(member)}
              >
                認証情報を削除
              </button>
            {/if}
          </KebabMenu>
        </td>
      </tr>
    {/each}
  </tbody>
</table>

<style>
  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  .data-table th,
  .data-table td {
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid var(--border);
  }
  .data-table th {
    font-size: 12px;
    color: var(--muted);
    font-weight: 600;
  }
  .actions {
    text-align: right;
    white-space: nowrap;
  }
  .danger {
    color: var(--danger, #c83232);
  }
  .badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
  }
  .badge + .badge {
    margin-left: 4px;
  }
  .status-invited {
    background: rgba(234, 179, 8, 0.15);
    color: #a16207;
  }
  .status-active {
    background: rgba(34, 197, 94, 0.15);
    color: #15803d;
  }
  .status-inactive {
    background: rgba(148, 163, 184, 0.2);
    color: #475569;
  }
  .auth0-member {
    background: rgba(34, 197, 94, 0.15);
    color: #15803d;
  }
  .auth0-nonmember {
    background: rgba(234, 179, 8, 0.15);
    color: #a16207;
  }
  .auth0-none {
    background: rgba(148, 163, 184, 0.2);
    color: #475569;
  }
  .auth0-blocked {
    background: rgba(200, 50, 50, 0.12);
    color: #c83232;
  }
</style>
